const KAKAO_LOCAL_ADDRESS_SEARCH_ENDPOINT =
  process.env.REACT_APP_KAKAO_LOCAL_ADDRESS_SEARCH_ENDPOINT ||
  'https://dapi.kakao.com/v2/local/search/address.json';
const KAKAO_REST_API_KEY = process.env.REACT_APP_KAKAO_REST_API_KEY || '';
const KAKAO_JAVASCRIPT_KEY =
  process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || process.env.REACT_APP_KAKAO_JS_KEY || '';
const KAKAO_MAP_SDK_SCRIPT_URL =
  process.env.REACT_APP_KAKAO_MAP_SDK_SCRIPT_URL ||
  `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(
    KAKAO_JAVASCRIPT_KEY,
  )}&libraries=services&autoload=false`;

let kakaoMapSdkLoadingPromise = null;

function parseCoordinateNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

async function loadKakaoMapSdk() {
  if (typeof window === 'undefined') {
    throw new Error('브라우저 환경에서만 주소 좌표 조회를 사용할 수 있습니다.');
  }
  if (!KAKAO_JAVASCRIPT_KEY) {
    throw new Error('REACT_APP_KAKAO_JAVASCRIPT_KEY 환경변수가 필요합니다.');
  }
  if (window.kakao?.maps?.services) {
    return window.kakao.maps;
  }
  if (kakaoMapSdkLoadingPromise) {
    return kakaoMapSdkLoadingPromise;
  }

  kakaoMapSdkLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-kakao-map-sdk-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (!window.kakao?.maps?.load) {
          reject(new Error('카카오 지도 SDK 초기화에 실패했습니다.'));
          return;
        }
        window.kakao.maps.load(() => {
          if (window.kakao?.maps?.services) {
            resolve(window.kakao.maps);
            return;
          }
          reject(new Error('카카오 지도 services 라이브러리를 사용할 수 없습니다.'));
        });
      });
      existingScript.addEventListener('error', () => {
        reject(new Error('카카오 지도 SDK 로드에 실패했습니다.'));
      });
      return;
    }

    const script = document.createElement('script');
    script.src = KAKAO_MAP_SDK_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.dataset.kakaoMapSdkScript = 'true';
    script.onload = () => {
      if (!window.kakao?.maps?.load) {
        reject(new Error('카카오 지도 SDK 초기화에 실패했습니다.'));
        return;
      }
      window.kakao.maps.load(() => {
        if (window.kakao?.maps?.services) {
          resolve(window.kakao.maps);
          return;
        }
        reject(new Error('카카오 지도 services 라이브러리를 사용할 수 없습니다.'));
      });
    };
    script.onerror = () => {
      reject(new Error('카카오 지도 SDK 로드에 실패했습니다.'));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    kakaoMapSdkLoadingPromise = null;
    throw error;
  });

  return kakaoMapSdkLoadingPromise;
}

async function searchAddressCoordinatesWithMapSdk(query) {
  const maps = await loadKakaoMapSdk();
  return new Promise((resolve, reject) => {
    const geocoder = new maps.services.Geocoder();
    geocoder.addressSearch(query, (result, status) => {
      if (status !== maps.services.Status.OK || !Array.isArray(result) || !result[0]) {
        reject(new Error('카카오 지도 Geocoder에서 좌표를 찾지 못했습니다.'));
        return;
      }

      const firstResult = result[0];
      const latitude = parseCoordinateNumber(firstResult.y);
      const longitude = parseCoordinateNumber(firstResult.x);
      if (latitude === null || longitude === null) {
        reject(new Error('좌표 변환 결과가 올바르지 않습니다.'));
        return;
      }

      resolve({
        address:
          String(firstResult.road_address_name || firstResult.address_name || query).trim() || query,
        latitude,
        longitude,
        roadAddress: String(firstResult.road_address_name || '').trim(),
        jibunAddress: String(firstResult.address_name || '').trim(),
      });
    });
  });
}

async function searchAddressCoordinatesWithRestApi(query) {
  if (!KAKAO_REST_API_KEY) {
    throw new Error('REACT_APP_KAKAO_REST_API_KEY 환경변수가 필요합니다.');
  }

  const searchParams = new URLSearchParams({
    query,
    analyze_type: 'exact',
  });
  const response = await fetch(
    `${KAKAO_LOCAL_ADDRESS_SEARCH_ENDPOINT}?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `KakaoAK ${KAKAO_REST_API_KEY}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error(
        '카카오 Local API 권한이 없습니다(403). Kakao Developers에서 카카오맵/Local 사용 설정과 앱키 타입을 확인해 주세요.',
      );
    }
    throw new Error('주소 검색 API 호출에 실패했습니다.');
  }

  const result = await response.json();
  const firstDocument = Array.isArray(result?.documents) ? result.documents[0] : null;
  if (!firstDocument) {
    throw new Error('해당 주소의 좌표를 찾지 못했습니다.');
  }

  const roadAddress = firstDocument.road_address || {};
  const jibunAddress = firstDocument.address || {};
  const latitude = parseCoordinateNumber(roadAddress.y ?? jibunAddress.y);
  const longitude = parseCoordinateNumber(roadAddress.x ?? jibunAddress.x);

  if (latitude === null || longitude === null) {
    throw new Error('좌표 변환 결과가 올바르지 않습니다.');
  }

  return {
    address:
      String(roadAddress.address_name || jibunAddress.address_name || query).trim() || query,
    latitude,
    longitude,
    roadAddress: String(roadAddress.address_name || '').trim(),
    jibunAddress: String(jibunAddress.address_name || '').trim(),
  };
}

export async function searchAddressCoordinates({ query }) {
  const normalizedQuery = String(query || '').trim();
  if (!normalizedQuery) {
    throw new Error('좌표를 찾을 주소를 입력해 주세요.');
  }

  if (KAKAO_JAVASCRIPT_KEY) {
    try {
      return await searchAddressCoordinatesWithMapSdk(normalizedQuery);
    } catch (error) {
      if (!KAKAO_REST_API_KEY) {
        throw error;
      }
    }
  }

  if (KAKAO_REST_API_KEY) {
    return searchAddressCoordinatesWithRestApi(normalizedQuery);
  }

  throw new Error(
    '주소 좌표 조회를 위해 REACT_APP_KAKAO_JAVASCRIPT_KEY 또는 REACT_APP_KAKAO_REST_API_KEY가 필요합니다.',
  );
}
